import { Injectable, OnDestroy } from '@angular/core';
import moment from 'moment';
import { interval, Observable, Subscription, throwError } from 'rxjs';
import { catchError, map, takeUntil, tap } from 'rxjs/operators';
import { ApBaseClass, GroupLockRequestResultDto } from 'webcommon/shared';
import { LockRepository, ServerUtilRepository } from 'webcommon/webapi';

@Injectable({
  providedIn: 'root',
})
export class ApLockService extends ApBaseClass implements OnDestroy {

  refreshTime = 60000; // 60 seconds
  refreshTimer$: Subscription;

  locks: {
    [instanceId: string]: GroupLockRequestResultDto | null;
  };

  constructor(
    private lockRepository: LockRepository,
    private serverUtilRepository: ServerUtilRepository,
  ) {
    super();

    this.locks = { };

    this.refreshTimer$ = interval(this.refreshTime).pipe(
      takeUntil(this.onDestroy$),
    ).subscribe(
      () => this.refreshLocks(),
    );
  }

  getDatabaseTime(): Observable<string> {
    return this.serverUtilRepository.getDatabaseTime();
  }

  getLock(visitId: string, lockType: string, loadDate: string): Observable<GroupLockRequestResultDto> {
    loadDate = encodeURI(loadDate);
    return this.lockRepository.putGroupLock(visitId, lockType, loadDate).pipe(
      tap((data) => this.storeLock(data)),
    );
  }

  releaseLock(lockInstanceId: string): Observable<string> {
    return this.lockRepository.putGroupUnlock(lockInstanceId).pipe(
      map(() => lockInstanceId),
      tap((data) => this.removeLock(data)),
    );
  }

  deleteLock(lockInstanceId: string): Observable<string> {
    return this.lockRepository.deleteLockGroup(lockInstanceId).pipe(
      map(() => lockInstanceId),
      tap((data) => this.removeLock(data)),
    );
  }

  refreshLock(currentLockDto: GroupLockRequestResultDto): Observable<GroupLockRequestResultDto> {
    return this.lockRepository.putGroupLock(
      currentLockDto.ObjectId,
      currentLockDto.GroupLockType,
      moment.utc().format(), // TODO see if this matters/can be in the dto
      currentLockDto.GroupInstanceId,
    ).pipe(
      catchError((error: unknown) => {
        // TODO If error,
        // TODO Should i try to get lock again or stop trying to refresh?
        // TODO Should I also do something else?
        // TODO In prm it doesn't do anything if refresh fails
        this.removeLock(currentLockDto.GroupInstanceId); // Removing lock on fail for now
        return throwError(error);
      }),
      tap((data) => this.storeLock(data)),
    );
  }

  private refreshLocks(): void {
    const emptyKeys: string[] = [];
    Object.keys(this.locks).forEach((key) => {
      if (this.locks[key]) {
        this.refreshLock((this.locks[key] as GroupLockRequestResultDto)).pipe(
          takeUntil(this.onDestroy$),
        ).subscribe(
          () => { /* refresh successful */ },
          () => { /* error refreshing */ },
        );
      } else {
        emptyKeys.push(key);
      }
    });
    if (emptyKeys.length) { // Clean up empty slots
      emptyKeys.forEach((key) => delete this.locks[key]);
    }
  }

  private removeLock(lockInstanceId: string): boolean {
    this.locks[lockInstanceId] = null;
    delete this.locks[lockInstanceId];
    return true;
  }

  private storeLock(data: GroupLockRequestResultDto): GroupLockRequestResultDto {
    this.locks[data.GroupInstanceId] = data;
    return data;
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    if (this.refreshTimer$) {
      this.refreshTimer$.unsubscribe();
    }
  }
}
